import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@mailflow/ui';
import { companiesQuery, useCreateCompany } from '../lib/companies.js';

export function CompaniesPage() {
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');

  const { data, isLoading } = useQuery(companiesQuery({ search }));
  const createCompany = useCreateCompany();

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    await createCompany.mutateAsync({
      name: name.trim(),
      domain: domain.trim() || undefined,
      websiteUrl: websiteUrl.trim() || undefined,
      phone: phone.trim() || undefined,
      city: city.trim() || undefined,
    });
    setName('');
    setDomain('');
    setWebsiteUrl('');
    setPhone('');
    setCity('');
    setShowModal(false);
  };

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <header className="page-header">
        <div>
          <h1 className="text-2xl">Firmen</h1>
          <p className="muted text-sm">
            Unternehmen mit Google-Unternehmensprofil, Kontakten und Sales-Deals.
          </p>
        </div>
        <Button onClick={() => setShowModal(true)}>Firma anlegen</Button>
      </header>

      <div className="toolbar">
        <input
          className="input"
          type="search"
          placeholder="Firma, Domain oder Ort suchen …"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: '320px' }}
        />
      </div>

      {isLoading ? (
        <p className="muted">Wird geladen …</p>
      ) : !data || data.items.length === 0 ? (
        <p className="muted">Keine Firmen vorhanden.</p>
      ) : (
        <div className="card">
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Firma</th>
                  <th>Ort</th>
                  <th>Website</th>
                  <th>Telefon</th>
                  <th>Bewertung</th>
                  <th>Anlagedatum</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((comp) => (
                  <tr key={comp.id}>
                    <td>
                      <Link to="/firmen/$id" params={{ id: comp.id }} style={{ fontWeight: 600 }}>
                        {comp.name}
                      </Link>
                      {comp.domain && <div className="muted text-sm">{comp.domain}</div>}
                    </td>
                    <td>{comp.city || '—'}</td>
                    <td>
                      {comp.websiteUrl ? (
                        <a href={comp.websiteUrl} target="_blank" rel="noopener noreferrer">
                          {comp.websiteUrl.replace(/^https?:\/\//, '')}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{comp.phone ? <a href={`tel:${comp.phone}`}>{comp.phone}</a> : '—'}</td>
                    <td>
                      {comp.rating ? (
                        <span>
                          ⭐ {comp.rating.toFixed(1)} ({comp.ratingCount ?? 0})
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{new Date(comp.createdAt).toLocaleDateString('de-CH')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-backdrop">
          <div className="modal card" style={{ maxWidth: '480px', margin: 'auto' }}>
            <div className="card__body stack">
              <h2>Neue Firma anlegen</h2>
              <form onSubmit={handleCreate} className="stack">
                <div>
                  <label className="label">Firmenname *</label>
                  <input
                    className="input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="label">Domain</label>
                  <input
                    className="input"
                    placeholder="example.com"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label">Website URL</label>
                  <input
                    className="input"
                    placeholder="https://example.com"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label">Telefon</label>
                  <input
                    className="input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label">Ort</label>
                  <input className="input" value={city} onChange={(e) => setCity(e.target.value)} />
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--space-2)',
                    justifyContent: 'flex-end',
                    marginTop: '12px',
                  }}
                >
                  <Button variant="secondary" type="button" onClick={() => setShowModal(false)}>
                    Abbrechen
                  </Button>
                  <Button type="submit" loading={createCompany.isPending}>
                    Speichern
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
